/*
    @class RichText
    @constructor 
    @param {Array} [textBlocks]
    Array of Objects:
        Each Object: {text: String, font: String, color: String}
*/

function RichText(markup, font, color) {
    this.DisplayObject_constructor();

    this._markup = markup;
    this._baseFont = font;
    this._color = color;

    this._textBlocks = this._MarkupToTextBlock();

    /**
     * The horizontal text alignment. Any of "start", "end", "left", "right", and "center". For detailed
     * information view the
     * <a href="http://www.whatwg.org/specs/web-apps/current-work/multipage/the-canvas-element.html#text-styles">
     * whatwg spec</a>. Default is "left".
     * @property textAlign
     * @type String
     **/
    this.textAlign = "left";

    /**
     * The vertical alignment point on the font. Any of "top", "hanging", "middle", "alphabetic", "ideographic", or
     * "bottom". For detailed information view the <a href="http://www.whatwg.org/specs/web-apps/current-work/multipage/the-canvas-element.html#text-styles">
     * whatwg spec</a>. Default is "top".
     * @property textBaseline
     * @type String
     */
    this.textBaseline = "top";

    /**
     *
     * The maximum width to draw the text. If maxWidth is specified (not null), the text will be condensed or
     * shrunk to make it fit in this width. For detailed information view the
     * <a href="http://www.whatwg.org/specs/web-apps/current-work/multipage/the-canvas-element.html#text-styles">
     * whatwg spec</a>.
     * @property maxWidth
     * @type Number
     */
    this.maxWidth = null;

    /**
     * If greater than 0, the text will be drawn as a stroke (outline) of the specified width.
     * @property outline
     * @type Number
     **/
    this.outline = 0;

    /**
     * Indicates the line height (vertical distance between baselines) for multi-line text. If null or 0,
     * the value of getMeasuredLineHeight is used.
     * @property lineHeight
     * @type Number
     **/
    this.lineHeight = 0;

    /**
     * Indicates the maximum width for a line of text before it is wrapped to multiple lines. If null,
     * the text will not be wrapped.
     * @property lineWidth
     * @type Number
     **/
    this.lineWidth = null;
}

var p = createjs.extend(RichText, createjs.DisplayObject);

// static properties:
/**
 * @property _workingContext
 * @type CanvasRenderingContext2D
 * @private
 **/

var canvas = createjs.createCanvas
    ? createjs.createCanvas()
    : document.createElement("canvas");
if (canvas.getContext) {
    Text._workingContext = canvas.getContext("2d");
    canvas.width = canvas.height = 1;
}

Object.defineProperty(p, "markup", {
    enumerable: true,
    get: function () {
        return this._markup;
    },
    set: function (markup) {
        this._markup = markup;
        this._textBlocks = this._MarkupToTextBlock();
    }
});
Object.defineProperty(p, "color", {
    enumerable: true,
    get: function () {
        return this._color;
    },
    set: function (color) {
        this._color = color;
        this._textBlocks = this._MarkupToTextBlock();
    }
});
Object.defineProperty(p, "font", {
    enumerable: true,
    get: function () {
        return this._baseFont;
    },
    set: function (font) {
        this._baseFont = font;
        this._textBlocks = this._MarkupToTextBlock();
    }
});

// constants:
/**
 * Lookup table for the ratio to offset bounds x calculations based on the textAlign property.
 * @property H_OFFSETS
 * @type Object
 * @protected
 * @static
 **/
RichText.H_OFFSETS = { start: 0, left: 0, center: -0.5, end: -1, right: -1 };

/**
 * Lookup table for the ratio to offset bounds y calculations based on the textBaseline property.
 * @property H_OFFSETS
 * @type Object
 * @protected
 * @static
 **/
RichText.V_OFFSETS = {
    top: 0,
    hanging: -0.01,
    middle: -0.4,
    alphabetic: -0.8,
    ideographic: -0.85,
    bottom: -1
};

/*  WordsToDraw Interface: Array of [Object]
            Where Object is:
            {
                width: number; (Represents width of text from ctx with the font applied to ctx)
                font: string;
                text: string;
            }

    */
p.draw = function (ctx, ignoreCache) {
    if (this.DisplayObject_draw(ctx, ignoreCache)) {
        return true;
    }

    let col = this._color || "#000";
    ctx.fillStyle = col;

    const wordsToDraw = this._textBlocks.reduce(
        function (acc, textBlock) {
            this._prepContext(ctx, textBlock);
            const words = String(textBlock.text).split(/(\s|\r\n|\r|\n)/);
            words.forEach(function (word) {
                acc.push({
                    width: ctx.measureText(word).width,
                    font: textBlock.font,
                    text: word,
                    color: textBlock.color
                });
            });
            return acc;
        }.bind(this),
        []
    );

    let lineHeight = this.lineHeight || this._getMaxHeight();
    let lineOfWords = [];
    let currentLineWidth = 0;
    let lineCount = 0;

    wordsToDraw.forEach(
        function (word) {
            if (
                (this.lineWidth && currentLineWidth + word.width > this.lineWidth) ||
                word.text === "\r\n" ||
                word.text === "\n" ||
                word.text === "\r"
            ) {
                this._drawWordLine(
                    ctx,
                    lineOfWords,
                    currentLineWidth,
                    lineCount * lineHeight,
                    this.lineWidth
                );
                lineOfWords = [];
                lineOfWords.push(word);
                currentLineWidth = word.width;
                lineCount++;
            } else {
                lineOfWords.push(word);
                currentLineWidth += word.width;
            }
        }.bind(this)
    );
    if (lineOfWords.length > 0)
        this._drawWordLine(
            ctx,
            lineOfWords,
            currentLineWidth,
            lineCount * lineHeight,
            this.lineWidth
        );

    return true;
};

p._drawWordLine = function (ctx, wordLine, width, y, maxLineWidth) {
    width = this._wordLineTrim(wordLine).reduce(function (acc, word) {
        return acc + word.width;
    }, 0);

    let x = 0;
    if (this.textAlign === "left") {
        x = 0;
    } else if (this.textAlign === "right") {
        x = -width;
    } else if (this.textAlign === "center") {
        x = -width / 2;
    }

    wordLine.forEach(
        function (word) {
            this._prepContext(ctx, {
                font: word.font,
                textAlign: "left",
                color: word.color
            });
            this._drawText(ctx, word.text, x, y);
            x += word.width;
        }.bind(this)
    );
};

if (!Array.prototype.findIndex) {
    Array.prototype.findIndex = function (func) {
        return this.reduce(function (acc, v, i) {
            if (acc >= 0) {
                return acc;
            }
            if (func(v)) {
                return i;
            }
            return acc;
        }, -1);
    };
}

p._wordLineTrim = function (wordLine) {
    let start = wordLine.findIndex(function (word) {
        return word.text.trim().length > 0;
    });
    wordLine.splice(0, start);
    wordLine.reverse();
    start = wordLine.findIndex(function (word) {
        return word.text.trim().length > 0;
    });
    wordLine.splice(0, start);
    wordLine.reverse();
    return wordLine;
};

p._drawText = function (ctx, text, x, y) {
    if (this.outline) {
        ctx.strokeText(text, x, y, this.maxWidth || 0xffff);
    } else {
        ctx.fillText(text, x, y, this.maxWidth || 0xffff);
    }
};

p._prepContext = function (ctx, textBlock) {
    ctx.font = textBlock.font || this._baseFont || "10px sans-serif";
    ctx.fillStyle = textBlock.color || this._color || "#000";
    ctx.textAlign = textBlock.textAlign || this.textAlign || "left";
    ctx.textBaseline = this.textBaseline || "top";
    ctx.lineJoin = "miter";
    ctx.miterLimit = 2.5;
    return ctx;
};

p.getMeasuredLineHeight = function () {
    return this._getMeasuredWidth("M") * 1.2;
};

/**
 * @method _getMeasuredWidth
 * @param {String} text
 * @protected
 **/
p._getMeasuredWidth = function (textBlock) {
    var ctx = Text._workingContext;
    ctx.save();
    var w = this._prepContext(ctx, textBlock).measureText(textBlock.text).width;
    ctx.restore();
    return w;
};

p._getMaxWidth = function () {
    return this._textBlocks.reduce(function (biggestWidth, textBlock) {
        var textBlockWidth = this._getMeasuredWidth({
            font: textBlock.font,
            text: "M"
        });
        return biggestWidth > textBlockWidth ? biggestWidth : textBlockWidth;
    }, 0);
};

p._getMaxHeight = function () {
    return this._getMaxWidth() * 1.2;
};

p._MarkupToTextBlock = function () {
    let markup = this._markup;
    let baseFont = this._baseFont;
    let color = this._color;

    function getToken(markup) {
        if (markup.slice(0, 2) === "<b" && markup.slice(-1) === ">") {
            return "boldOpen";
        } else if (markup === "</b>") {
            return "boldClose";
        } else if (markup.slice(0, 2) === "<i" && markup.slice(-1) === ">") {
            return "italicOpen";
        } else if (markup === "</i>") {
            return "italicClose";
        } else {
            return "text";
        }
    }

    function getColor(markup) {
        return markup
            .split("=")
            .slice(1)[0]
            .replace(/>/g, "")
            .replace(/('|")/g, "")
            .split(":")
            .slice(1)[0]
            .replace(/;/g, "")
            .trim();
    }

    var tags = null;
    try {
        var splitMarkup = markup.split(/(<b[^>]*>|<\/b>|<i[^>]*>|<\/i>)/);
        var state = "text";
        tags = splitMarkup.reduce(function (acc, text, index) {
            if (text === "") {
                return acc;
            }

            var type = getToken(text);
            if (state === "text") {
                if (type === "boldOpen") {
                    state = "bold";
                } else if (type === "italicOpen") {
                    state = "italic";
                } else if (state === "text" && type === "text") {
                    acc.push({ font: baseFont, color: color, text: text });
                } else {
                    throw "Invalid markup looking for text and found a " + text + " tag";
                }
            } else if (state === "bold") {
                if (type === "text") {
                    const oldColor = color;
                    color = index - 1 >= 0 ? getColor(splitMarkup[index - 1]) : color;
                    acc.push({ font: "bold " + baseFont, color: color, text: text });
                    color = oldColor;
                } else if (type === "italicOpen") {
                    state = "bold italic";
                } else if (type === "boldClose") {
                    state = "text";
                } else {
                    throw "Invalid markup looking for text or closing bold tag and found a " +
                    text +
                    " tag";
                }
            } else if (state === "italic") {
                if (type === "text") {
                    const oldColor = color;
                    color = index - 1 >= 0 ? getColor(splitMarkup[index - 1]) : color;
                    acc.push({ font: "italic " + baseFont, color: color, text: text });
                    color = oldColor;
                } else if (type === "boldOpen") {
                    state = "bold italic";
                } else if (type === "italicClose") {
                    state = "text";
                } else {
                    throw "Invalid markup looking for text or closing italic tag and found a " +
                    text +
                    " tag";
                }
            } else if (state === "bold italic") {
                if (type === "text") {
                    const oldColor = color;
                    color = index - 1 >= 0 ? getColor(splitMarkup[index - 1]) : color;
                    acc.push({
                        font: "bold italic " + baseFont,
                        color: color,
                        text: text
                    });
                    color = oldColor;
                } else if (type === "boldClose") {
                    state = "italic";
                } else if (type === "italicClose") {
                    state = "bold";
                } else {
                    throw "Invalid markup looking for text or closing italic or bold tag and found a " +
                    text +
                    " tag instead";
                }
            }
            return acc;
        }, []);
    } catch (e) {
        throw e;
    }
    this._textBlocks = tags;
    return tags;
};

createjs.RichText = createjs.promote(RichText, "DisplayObject");